// screens/ScoreKeeper.js - Gestione del punteggio durante l'incontro

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Modal, Alert } from 'react-native';
import { useLocation, useNavigate } from 'react-router-dom';
import { loadMatches, saveMatches, loadTeams, saveBracket } from '../utils/storage';
import { generateAssaltiFederali } from '../utils/fencingLogic';
import { generateAssaltiCustomBottomUp } from '../utils/generateAssaltiCustomBottomUp';
import { useInversioneLati } from '../hooks/useInversioneLati';
import InvertiLatiButton from '../components/InvertiLatiButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SettingsIcon from '@mui/icons-material/Settings';
import { FencingSwordsIcon } from '../components/FencingIcons';

// Nuovi componenti modulari
import { NumpadModal, NextAssaltoModal, PriorityModal } from '../components/scorekeeper';
import MatchManagerModal from '../components/scorekeeper/MatchManagerModal';
import BoutSummaryModal from '../components/scorekeeper/BoutSummaryModal';
import { useScoreLogic } from '../hooks/useScoreLogic';

// Rimosso componente AutoScalingText che causava problemi di compilazione

const ScoreKeeper = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // --- Hook Logica Punteggio
  // --- Stati locali aggiuntivi (alcuni per compatibilità legacy)
  const [stoccatePerAssaltoLocal, setStoccatePerAssalto] = useState(location.state?.stoccatePerAssalto || 5);

  // --- Hook Logica Punteggio
  const stoccatePerAssalto = stoccatePerAssaltoLocal;

  // Recupera variabili da location.state
  const matchId = location.state?.matchId;
  const matchIdx = location.state?.matchIdx;
  const roundIdx = location.state?.roundIdx;
  const bracketMode = location.state?.bracketMode || false;
  const matchRealId = location.state?.matchRealId;

  const [match, setMatch] = useState(location.state?.match || null);
  const [assalti, setAssalti] = useState(location.state?.assalti || location.state?.match?.assalti || []);
  const [currentAssalto, setCurrentAssalto] = useState(location.state?.currentAssalto || 0);

  const {
    scoreA, scoreB,
    setScoreA, setScoreB,
    updateMatchScore,
    getStoccateTotali,
    getThresholds,
    isMatchFinished,
    increaseScoreA, decreaseScoreA, increaseScoreB, decreaseScoreB
  } = useScoreLogic({
    matchId, bracketMode, roundIdx, matchIdx, assalti, stoccatePerAssalto: stoccatePerAssaltoLocal, match
  });

  const handleScoreChange = useCallback((team, delta) => {
    if (team === 'A') {
      if (delta > 0) increaseScoreA();
      else decreaseScoreA();
    } else if (team === 'B') {
      if (delta > 0) increaseScoreB();
      else decreaseScoreB();
    }
  }, [increaseScoreA, decreaseScoreA, increaseScoreB, decreaseScoreB]);


  // --- Stati locali aggiuntivi (alcuni per compatibilità legacy)
  const [teams, setTeams] = useState([]);
  const [teamA, setTeamA] = useState(null);
  const [teamB, setTeamB] = useState(null);

  // Usa l'hook per gestire l'inversione lati
  const { invertiLati, toggleInvertiLati, getLato, getAssaltoInvertito } = useInversioneLati(matchRealId);
  const [activeTeamInput, setActiveTeamInput] = useState(null);
  const [showNumpad, setShowNumpad] = useState(false);
  const [penalties, setPenalties] = useState({ A: { yellow: 0, red: 0 }, B: { yellow: 0, red: 0 } });
  const [activeCardColor, setActiveCardColor] = useState(null);
  const [lastModifiedTeam, setLastModifiedTeam] = useState(null);
  const [priorityTeam, setPriorityTeam] = useState(null);
  const [showMatchManager, setShowMatchManager] = useState(false);
  const [sostituzioni, setSostituzioni] = useState([]);
  const [showNextAssaltoModal, setShowNextAssaltoModal] = useState(false);
  const [showBoutSummary, setShowBoutSummary] = useState(false);
  const [boutSummaryData, setBoutSummaryData] = useState(null);
  const [showPriorityModal, setShowPriorityModal] = useState(false);
  const isInitialized = useRef(false);

  // Funzione per assegnare manualmente la priorità
  const assignPriority = useCallback((team) => {
    if (scoreA === scoreB) {
      setPriorityTeam(team);
      return team;
    }
    return null;
  }, [scoreA, scoreB]);

  // Wrapper per gestire l'evidenziazione dell'ultimo cambio
  const handleScoreChangeWithAssaltoCheck = useCallback((fencer, delta) => {
    handleScoreChange(fencer, delta);
    setLastModifiedTeam(fencer);
    setTimeout(() => setLastModifiedTeam(null), 2000);
  }, [handleScoreChange]);

  // Funzione per calcolare le statistiche totali del match (punti per ogni tiratore)
  const calculateMatchStats = useCallback((finalAssalti = assalti) => {
    const stats = { A: {}, B: {} };
    finalAssalti.forEach((assalto, idx) => {
      const pA = (assalto.scoreA || 0) - (idx > 0 ? (finalAssalti[idx - 1].scoreA || 0) : 0);
      const pB = (assalto.scoreB || 0) - (idx > 0 ? (finalAssalti[idx - 1].scoreB || 0) : 0);
      const nameA = typeof assalto.fencerA === 'string' ? assalto.fencerA : assalto.fencerA?.name || 'Unknown';
      const nameB = typeof assalto.fencerB === 'string' ? assalto.fencerB : assalto.fencerB?.name || 'Unknown';
      if (pA > 0) stats.A[nameA] = (stats.A[nameA] || 0) + pA;
      if (pB > 0) stats.B[nameB] = (stats.B[nameB] || 0) + pB;
    });
    return stats;
  }, [assalti]);


  // Funzione per passare all'assalto successivo quando il tempo scade
  const handleNextAssalto = useCallback(() => {
    // Aggiorna l'assalto corrente solo se non è l'ultimo
    if (currentAssalto < assalti.length - 1) {
      // Calcola i punti fatti in questo assalto
      // I punti fatti in questo assalto sono la differenza dal threshold precedente
      // Per calcolare i punti di ciascun tiratore, usiamo il punteggio parziale dell'assalto
      const currentAssaltoData = assalti[currentAssalto];
      // const prevAssaltoData = currentAssalto > 0 ? assalti[currentAssalto - 1] : null;

      // Punti fatti in questo assalto = punteggio attuale - punteggio alla fine assalto precedente
      const pointsInBoutA = scoreA - (currentAssalto > 0 ? (assalti[currentAssalto - 1]?.scoreA || 0) : 0);
      const pointsInBoutB = scoreB - (currentAssalto > 0 ? (assalti[currentAssalto - 1]?.scoreB || 0) : 0);

      // Dati tiratori correnti e prossimi
      const currentFencerA = currentAssaltoData?.fencerA || currentAssaltoData?.actualFencerA || '';
      const currentFencerB = currentAssaltoData?.fencerB || currentAssaltoData?.actualFencerB || '';

      const nextAssaltoData = assalti[currentAssalto + 1];
      const nextFencerA = nextAssaltoData?.fencerA || nextAssaltoData?.actualFencerA || null;
      const nextFencerB = nextAssaltoData?.fencerB || nextAssaltoData?.actualFencerB || null;

      // Salva i punteggi dell'assalto corrente
      const updatedAssalti = [...assalti];
      if (updatedAssalti[currentAssalto]) {
        updatedAssalti[currentAssalto].scoreA = scoreA;
        updatedAssalti[currentAssalto].scoreB = scoreB;
      }

      // Mostra il popup riepilogo solo se è stato segnato almeno un punto e NON è individuale
      const competitionType = localStorage.getItem('competitionType') || 'individual';
      if (competitionType !== 'individual' && (scoreA > 0 || scoreB > 0)) {
        setBoutSummaryData({
          boutNumber: currentAssalto + 1,
          fencerA: typeof currentFencerA === 'string' ? currentFencerA : currentFencerA?.name || '',
          fencerB: typeof currentFencerB === 'string' ? currentFencerB : currentFencerB?.name || '',
          pointsA: Math.max(0, pointsInBoutA),
          pointsB: Math.max(0, pointsInBoutB),
          nextFencerA: nextFencerA ? (typeof nextFencerA === 'string' ? nextFencerA : nextFencerA?.name) : null,
          nextFencerB: nextFencerB ? (typeof nextFencerB === 'string' ? nextFencerB : nextFencerB?.name) : null,
          totalScoreA: scoreA,
          totalScoreB: scoreB,
          matchStats: calculateMatchStats(updatedAssalti),
          isFinal: false
        });
        setShowBoutSummary(true);
      }

      // Passa all'assalto successivo
      setCurrentAssalto(currentAssalto + 1);

      // Salva gli assalti aggiornati nel match
      if (bracketMode) {
        const bracket = JSON.parse(localStorage.getItem('bracket') || '[]');
        if (Array.isArray(bracket) && bracket[roundIdx] && bracket[roundIdx][matchIdx]) {
          bracket[roundIdx][matchIdx].assalti = updatedAssalti;
          bracket[roundIdx][matchIdx].currentAssalto = currentAssalto + 1;
          saveBracket(bracket);
        }
      } else if (typeof matchId === 'number') {
        const matches = loadMatches() || [];
        matches[matchId] = {
          ...matches[matchId],
          assalti: updatedAssalti,
          currentAssalto: currentAssalto + 1
        };
        saveMatches(matches);
      }

      // Aggiorna l'interfaccia
      window.dispatchEvent(new Event('matchesUpdated'));
    }
  }, [currentAssalto, assalti, bracketMode, roundIdx, matchIdx, matchId, scoreA, scoreB, calculateMatchStats, setCurrentAssalto]);

  // Calcola la prossima soglia per cambio assalto
  const getNextThreshold = useCallback(() => {
    const maxScore = Math.max(scoreA, scoreB);
    const thresholds = getThresholds();
    return thresholds.find(t => t > maxScore) || getStoccateTotali();
  }, [scoreA, scoreB, getThresholds, getStoccateTotali]);

  // Carica teams solo una volta all’avvio
  useEffect(() => {
    setTeams(loadTeams() || []);
  }, []);

  // --- Caricamento squadre e match ---
  useEffect(() => {
    if (bracketMode && typeof roundIdx === 'number' && typeof matchIdx === 'number') {
      const bracket = JSON.parse(localStorage.getItem('bracket') || '[]');
      if (Array.isArray(bracket) && bracket[roundIdx] && bracket[roundIdx][matchIdx]) {
        const m = bracket[roundIdx][matchIdx];
        setMatch(m);
        // Valorizza sempre il nome squadra se manca E recupera membri se mancano (CRITICO per assalti team)
        let tA = m.team1 || {};
        let tB = m.team2 || {};

        if (Array.isArray(teams) && teams.length > 0) {
          // Check Team A
          if (!tA.name || !tA.members || !Array.isArray(tA.members)) {
            // Priority: ID match > Name match
            let found = tA.id !== undefined ? teams.find(t => t.id === tA.id) : null;
            if (!found && tA.name) found = teams.find(t => t.name === tA.name);

            if (found) {
              console.log(`[ScoreKeeper] Reloaded Team A details: ${found.name} (had members: ${!!tA.members})`);
              tA = found;
            }
          }
          // Check Team B
          if (!tB.name || !tB.members || !Array.isArray(tB.members)) {
            let found = tB.id !== undefined ? teams.find(t => t.id === tB.id) : null;
            if (!found && tB.name) found = teams.find(t => t.name === tB.name);

            if (found) {
              console.log(`[ScoreKeeper] Reloaded Team B details: ${found.name} (had members: ${!!tB.members})`);
              tB = found;
            }
          }
        }
        setTeamA(tA);
        setTeamB(tB);
        // Imposta i punteggi
        if (typeof m.score1 === 'number') {
          setScoreA(m.score1);
        } else if (typeof m.scoreA === 'number') {
          setScoreA(m.scoreA);
        }

        if (typeof m.score2 === 'number') {
          setScoreB(m.score2);
        } else if (typeof m.scoreB === 'number') {
          setScoreB(m.scoreB);
        }

        // Recupera assalti se esistono
        if (Array.isArray(m.assalti) && m.assalti.length > 0) {
          setAssalti(m.assalti);
        } else if (tA && tB && Array.isArray(tA.members) && Array.isArray(tB.members)) {
          const generated = generateAssaltiFederali(tA.members, tB.members);
          setAssalti(generated);
        } else {
          setAssalti([]);
        }
        // --- PATCH: riabilitato stoccatePerAssalto dal match (per staffetta) ---
        const competitionType = localStorage.getItem('competitionType') || 'individual';
        if (competitionType === 'individual') {
          // Usa le impostazioni dal localStorage: in bracketMode è sempre eliminazione diretta
          const configKey = 'individual_stoccateElimDiretta';
          const dynamicStoccate = parseInt(localStorage.getItem(configKey)) || 15;
          console.log(`[ScoreKeeper] Individual mode (Bracket): Using dynamic stoccate from config (${configKey}):`, dynamicStoccate);
          setStoccatePerAssalto(dynamicStoccate);
        } else if (typeof m.stoccatePerAssalto === 'number') {
          console.log("[ScoreKeeper] Using match specific stoccatePerAssalto:", m.stoccatePerAssalto);
          setStoccatePerAssalto(m.stoccatePerAssalto);
        }
      }
    } else if (typeof matchId === 'number') {
      const matches = loadMatches() || [];
      const m = matches[matchId];
      setMatch(m);
      // PATCH: valorizza sempre il nome squadra se manca
      // PATCH: robustezza nomi squadre modalità mixed
      let tA = {};
      let tB = {};
      const tipo = localStorage.getItem('competitionType');
      if (tipo === 'mixed') {
        // Cerca prima per indice numerico, poi per nome
        if (typeof m?.teamA === 'number' && teams[m.teamA]) tA = teams[m.teamA];
        else if (typeof m?.teamA === 'string') tA = teams.find(t => t.name === m.teamA) || {};
        else if (m?.squadraA) tA = teams.find(t => t.name === m.squadraA) || {};
        else if (m?.team1) tA = m.team1;
        if (typeof m?.teamB === 'number' && teams[m.teamB]) tB = teams[m.teamB];
        else if (typeof m?.teamB === 'string') tB = teams.find(t => t.name === m.teamB) || {};
        else if (m?.squadraB) tB = teams.find(t => t.name === m.squadraB) || {};
        else if (m?.team2) tB = m.team2;
      } else {
        tA = teams[m?.teamA] || m?.team1 || {};
        tB = teams[m?.teamB] || m?.team2 || {};
        if (!tA?.name && Array.isArray(teams) && teams.length > 0) {
          tA = teams.find(t => t.id === m?.teamA || t.name === m?.team1?.name) || tA;
        }
        if (!tB?.name && Array.isArray(teams) && teams.length > 0) {
          tB = teams.find(t => t.id === m?.teamB || t.name === m?.team2?.name) || tB;
        }
      }
      setTeamA(tA);
      setTeamB(tB);
      if (m && Array.isArray(teams)) {
        if (Array.isArray(m.assalti) && m.assalti.length > 0) {
          setAssalti(m.assalti);
        } else if (
          tA && tB &&
          Array.isArray(tA.members) && Array.isArray(tB.members)
        ) {
          const generated = generateAssaltiFederali(tA.members, tB.members);
          setAssalti(generated);
        } else {
          setAssalti([]);
        }
        setScoreA(m.scoreA || 0);
        setScoreB(m.scoreB || 0);
        // Imposta l'assalto corrente se è specificato nel match
        if (typeof m.currentAssalto === 'number') {
          setCurrentAssalto(m.currentAssalto);
        } else {
          setCurrentAssalto(0);
        }
        // --- PATCH: riabilitato stoccatePerAssalto dal match (per staffetta) ---
        // Nelle gare INDIVIDUALI, ignoriamo il valore salvato nel match per permettere updates dinamici dalla config
        const competitionType = localStorage.getItem('competitionType') || 'individual';
        if (competitionType === 'individual') {
          // Usa le impostazioni dal localStorage: distinguendo tra eliminazione diretta e girone
          const isElim = bracketMode || m.isEliminationMatch || m.round !== undefined;
          const configKey = isElim ? 'individual_stoccateElimDiretta' : 'individual_stoccateGirone';
          const dynamicStoccate = parseInt(localStorage.getItem(configKey)) || (isElim ? 15 : 5);
          console.log(`[ScoreKeeper] Individual mode: Using dynamic stoccate from config (${configKey}):`, dynamicStoccate);
          setStoccatePerAssalto(dynamicStoccate);
        } else if (typeof m.stoccatePerAssalto === 'number') {
          console.log("[ScoreKeeper] Using match specific stoccatePerAssalto:", m.stoccatePerAssalto);
          setStoccatePerAssalto(m.stoccatePerAssalto);
        }
      }
    }
  }, [bracketMode, roundIdx, matchIdx, matchId, teams, setAssalti, setCurrentAssalto, setMatch, setScoreA, setScoreB]);

  // Caricamento dati match dal localStorage
  useEffect(() => {
    if (!match) return;
    if (bracketMode && typeof roundIdx === 'number' && typeof matchIdx === 'number') {
      const bracket = JSON.parse(localStorage.getItem('bracket') || '[]');
      if (Array.isArray(bracket) && bracket[roundIdx] && bracket[roundIdx][matchIdx]) {
        const updated = { ...bracket[roundIdx][matchIdx], score1: scoreA, score2: scoreB };
        bracket[roundIdx][matchIdx] = updated;
        bracket[roundIdx][matchIdx].scoreA = scoreA;
        bracket[roundIdx][matchIdx].scoreB = scoreB;
        saveBracket(bracket);
      }
    } else if (typeof matchId === 'number') {
      const matches = loadMatches() || [];
      matches[matchId] = { ...matches[matchId], scoreA, scoreB };
      saveMatches(matches);

      // Aggiorna anche lo stato locale
      setScoreA(scoreA);
      setScoreB(scoreB);
    }
    window.dispatchEvent(new Event('matchesUpdated'));
  }, [scoreA, scoreB, match, bracketMode, roundIdx, matchIdx, matchId, setScoreA, setScoreB]);

  // NUOVO: Caricamento team da localStorage se non disponibili
  useEffect(() => {
    if ((!teamA || !teamB) && match) {
      console.log("[ScoreKeeper] Tentativo di caricamento team da localStorage per match:", match.id);
      const loadedTeams = loadTeams();
      console.log("[ScoreKeeper] Team caricati da localStorage:", loadedTeams.length);

      if (loadedTeams.length > 0) {
        // Cerca team per nome
        const teamAFromStorage = loadedTeams.find(t => t.name === match.squadraA);
        const teamBFromStorage = loadedTeams.find(t => t.name === match.squadraB);

        console.log("[ScoreKeeper] Team trovati:", {
          teamA: teamAFromStorage?.name,
          teamB: teamBFromStorage?.name
        });

        if (teamAFromStorage) setTeamA(teamAFromStorage);
        if (teamBFromStorage) setTeamB(teamBFromStorage);
      }
    }
  }, [teamA, teamB, match, setTeamA, setTeamB]);

  // --- Genera assalti se mancanti ---
  useEffect(() => {
    // Se abbiamo già assalti dal caricamento match, non rigenerare
    if (match?.assalti && match.assalti.length > 0 && assalti.length === 0) {
      console.log("[ScoreKeeper] Usando assalti dal match:", match.assalti.length);
      setAssalti(match.assalti);
      return;
    }

    // Generiamo assalti solo se non esistono già
    if (assalti.length === 0) {
      // Controlla se è un match misto con categorie multiple (singolo match)
      const competitionType = localStorage.getItem('competitionType');
      const isMixedMultiCategory = competitionType === 'mixed' && match && match.categoria && match.categoria.includes(',');

      console.log("[ScoreKeeper] Generazione assalti:", {
        competitionType,
        categoria: match?.categoria,
        isMixedMultiCategory,
        match,
        teamA,
        teamB
      });

      if (isMixedMultiCategory) {
        // Caso speciale: match misto con categorie unite - genera un solo assalto
        console.log("[ScoreKeeper] Caso speciale: match misto con categorie unite - genero UN SOLO ASSALTO");

        // Ottieni i nomi delle squadre dal match stesso
        const squadraA = match?.squadraA || teamA?.name || 'Squadra A';
        const squadraB = match?.squadraB || teamB?.name || 'Squadra B';

        console.log(`[ScoreKeeper] Creando assalto per ${squadraA} vs ${squadraB}`);

        // Nelle competizioni miste con categorie unite, fencerA/B sono i nomi delle squadre
        // Non duplicare i nomi in squadraA/B (usati solo per riferimento interno)
        const singleAssalto = [{
          fencerA: squadraA,  // Questo è ciò che verrà visualizzato
          fencerB: squadraB,  // Questo è ciò che verrà visualizzato
          id: '0_0',
          scoreA: 0,
          scoreB: 0
        }];
        setAssalti(singleAssalto);
      } else if (teamA && teamB && Array.isArray(teamA.members) && Array.isArray(teamB.members)) {
        // Verifica se è una staffetta 4vs4 (usa la funzione custom)
        const membersA = teamA.members;
        const membersB = teamB.members;
        const categoria = match?.categoria || 'Default';

        // Usa la funzione custom per staffetta 4vs4
        if (competitionType === 'mixed' && membersA.length === 4 && membersB.length === 4) {
          console.log("[ScoreKeeper] Generazione custom bottom-up per staffetta 4vs4");
          const generated = generateAssaltiCustomBottomUp(
            membersA,
            membersB,
            teamA.name,
            teamB.name,
            categoria,
            stoccatePerAssalto
          );
          console.log("[ScoreKeeper] Assalti custom generati:", JSON.stringify(generated, null, 2));
          setAssalti(generated);
        } else {
          // Comportamento standard per tutte le altre tipologie
          console.log("[ScoreKeeper] Generazione standard assalti federali");
          const generated = generateAssaltiFederali(membersA, membersB);
          // Aggiungi dati squadre
          const assaltiConSquadre = generated.map(assalto => ({
            ...assalto,
            squadraA: teamA.name,
            squadraB: teamB.name
          }));
          setAssalti(assaltiConSquadre);
        }
      } else {
        console.log("[ScoreKeeper] Impossibile generare assalti: dati squadre insufficienti");

        // Assalto di fallback se ci sono almeno i nomi delle squadre nel match
        if (match && (match.squadraA || match.squadraB)) {
          const squadraA = match.squadraA || 'Squadra A';
          const squadraB = match.squadraB || 'Squadra B';
          console.log(`[ScoreKeeper] Creando assalto di fallback per ${squadraA} vs ${squadraB}`);

          const fallbackAssalto = [{
            fencerA: squadraA,
            fencerB: squadraB,
            squadraA: squadraA,
            squadraB: squadraB,
            id: 'fallback_0',
            scoreA: 0,
            scoreB: 0
          }];
          setAssalti(fallbackAssalto);
        }
      }
    }
  }, [assalti, teamA, teamB, match, setAssalti, stoccatePerAssalto]);

  // --- Calcoli UI ---
  useEffect(() => {
    const stoccateTotali = getStoccateTotali();
    const thresholds = getThresholds();
    const maxScore = Math.max(scoreA, scoreB);
    const nextThreshold = getNextThreshold();
    const isFineIncontro = isMatchFinished();

    // DEBUG LOG
    console.log('[ScoreKeeper] Cambio assalto:');
    console.log('  scoreA:', scoreA, 'scoreB:', scoreB);
    console.log('  thresholds:', thresholds);
    console.log('  maxScore:', maxScore);
    console.log('  nextThreshold:', nextThreshold);
    console.log('  assalti.length:', assalti.length);
    console.log('  stoccatePerAssalto:', stoccatePerAssalto);
    console.log('  stoccateTotali:', stoccateTotali);
    console.log('  isFineIncontro:', isFineIncontro);

    // Aggiorna currentAssalto in base al punteggio
    let newAssalto = 0;
    for (let i = 0; i < thresholds.length; i++) {
      if (scoreA < thresholds[i] && scoreB < thresholds[i]) {
        newAssalto = i;
        break;
      }
      if (scoreA >= thresholds[i] || scoreB >= thresholds[i]) {
        newAssalto = i + 1;
      }
    }
    if (newAssalto >= assalti.length) newAssalto = assalti.length - 1;
    console.log('  newAssalto (calcolato):', newAssalto, 'currentAssalto (attuale):', currentAssalto);

    // Se il nuovo assalto calcolato è superiore a quello attuale, avanziamo.
    // Se è inferiore (es. correzione punteggio), manteniamo quello attuale 
    // per non interrompere la sequenza se terminata per tempo.
    const finalNewAssalto = Math.max(currentAssalto, newAssalto);

    // Se siamo passati a un nuovo assalto (e non è l'ultimo), mostra il popup riepilogo
    if (finalNewAssalto > currentAssalto && currentAssalto < assalti.length - 1) {
      const finishedAssaltoData = assalti[currentAssalto];
      const nextAssaltoData = assalti[finalNewAssalto];

      // Salva i punteggi dell'assalto appena finito nell'array
      const updatedAssalti = [...assalti];
      if (updatedAssalti[currentAssalto]) {
        updatedAssalti[currentAssalto].scoreA = scoreA;
        updatedAssalti[currentAssalto].scoreB = scoreB;
      }
      setAssalti(updatedAssalti);

      // Calcola i punti fatti nell'assalto appena finito
      let startScoreA = 0;
      let startScoreB = 0;
      if (currentAssalto > 0 && updatedAssalti[currentAssalto - 1]) {
        startScoreA = updatedAssalti[currentAssalto - 1].scoreA || 0;
        startScoreB = updatedAssalti[currentAssalto - 1].scoreB || 0;
      }

      const pointsInBoutA = scoreA - startScoreA;
      const pointsInBoutB = scoreB - startScoreB;

      console.log('[BoutSummary] Calcolo punti:', {
        currentAssalto,
        startScoreA, startScoreB,
        endScoreA: scoreA, endScoreB: scoreB,
        pointsInBoutA, pointsInBoutB
      });

      // Dati tiratori
      const currentFencerA = finishedAssaltoData?.fencerA || finishedAssaltoData?.actualFencerA || '';
      const currentFencerB = finishedAssaltoData?.fencerB || finishedAssaltoData?.actualFencerB || '';
      const nextFencerA = nextAssaltoData?.fencerA || nextAssaltoData?.actualFencerA || null;
      const nextFencerB = nextAssaltoData?.fencerB || nextAssaltoData?.actualFencerB || null;

      // Mostra il popup riepilogo solo se è stato segnato almeno un punto e NON è individuale
      // E lo inibiamo se siamo ancora in fase di caricamento iniziale
      const competitionType = localStorage.getItem('competitionType') || 'individual';
      if (isInitialized.current && competitionType !== 'individual' && (scoreA > 0 || scoreB > 0)) {
        setBoutSummaryData({
          boutNumber: currentAssalto + 1,
          fencerA: typeof currentFencerA === 'string' ? currentFencerA : currentFencerA?.name || '',
          fencerB: typeof currentFencerB === 'string' ? currentFencerB : currentFencerB?.name || '',
          pointsA: Math.max(0, pointsInBoutA),
          pointsB: Math.max(0, pointsInBoutB),
          nextFencerA: nextFencerA ? (typeof nextFencerA === 'string' ? nextFencerA : nextFencerA?.name) : null,
          nextFencerB: nextFencerB ? (typeof nextFencerB === 'string' ? nextFencerB : nextFencerB?.name) : null,
          totalScoreA: scoreA,
          totalScoreB: scoreB,
          matchStats: calculateMatchStats(updatedAssalti),
          isFinal: false
        });
        setShowBoutSummary(true);
      }
    }

    setCurrentAssalto(finalNewAssalto);

    // Segna come inizializzato dopo il primo calcolo UI con dati reali
    if (teamA && teamB) {
      isInitialized.current = true;
    }
  }, [
    scoreA, scoreB, assalti, stoccatePerAssalto, currentAssalto,
    getThresholds, getNextThreshold, getStoccateTotali, isMatchFinished,
    calculateMatchStats, teamA, teamB, setAssalti, setCurrentAssalto
  ]);

  // --- FUNZIONI DI GESTIONE PUNTEGGIO (LEGACY RIMOSTE, USARE useScoreLogic) ---

  const handlePenalty = useCallback((fencer, type) => {
    console.log('Assegnato cartellino:', type);

    // Applica l'inversione lati se attiva
    const realFencer = invertiLati ? (fencer === 'A' ? 'B' : 'A') : fencer;
    const opponent = realFencer === 'A' ? 'B' : 'A';
    const newPenalties = { ...penalties };

    const colors = {
      yellow: '#ffeb3b',
      red: '#f44336'
    };

    const isYellowAlreadyPresent = newPenalties[realFencer].yellow > 0;

    if (type === 'yellow') {
      if (isYellowAlreadyPresent) {
        newPenalties[realFencer].red++;
        setActiveCardColor(colors.red);
        handleScoreChange(opponent, 1);
      } else {
        newPenalties[realFencer].yellow++;
        setActiveCardColor(colors.yellow);
      }
    } else {
      newPenalties[realFencer].red++;
      setActiveCardColor(colors.red);
      handleScoreChange(opponent, 1);
    }

    setPenalties(newPenalties);
  }, [penalties, handleScoreChange, invertiLati]);




  // --- FINE FUNZIONI PUNTEGGIO ---

  // --- Gestione input da tastierino numerico
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Controlla se è un tasto numerico (sia riga superiore che tastierino numerico)
      const isNumKey = (e.key >= '0' && e.key <= '9') ||
        (e.code.startsWith('Numpad') && !isNaN(parseInt(e.code.replace('Numpad', ''))));

      if (isNumKey) {
        let digit;
        // Estrai il numero sia da key che da code (per compatibilità con diversi browser/tastiere)
        if (e.key >= '0' && e.key <= '9') {
          digit = e.key;
        } else if (e.code.startsWith('Numpad')) {
          digit = e.code.replace('Numpad', '');
        }

        const numValue = parseInt(digit);

        if (!isNaN(numValue)) {
          console.log(`Input numerico rilevato: ${numValue} per squadra ${activeTeamInput}`);

          // Gestione input per squadra A
          if (activeTeamInput === 'A') {
            const maxScoreA = getStoccateTotali();
            if (numValue <= maxScoreA) {
              setScoreA(numValue);
              setLastModifiedTeam('A');
              setTimeout(() => setLastModifiedTeam(null), 2000);
            }
          }
          // Gestione input per squadra B
          else if (activeTeamInput === 'B') {
            const maxScoreB = getStoccateTotali();
            if (numValue <= maxScoreB) {
              setScoreB(numValue);
              setLastModifiedTeam('B');
              setTimeout(() => setLastModifiedTeam(null), 2000);
            }
          }

        }
      }
    };

    // Aggiungere listener per catturare gli eventi da tastiera
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    activeTeamInput, scoreA, scoreB, updateMatchScore,
    getStoccateTotali, setScoreA, setScoreB
  ]);

  // Funzione per aprire il tastierino numerico
  const openNumpad = (team) => {
    setActiveTeamInput(team);
    setLastModifiedTeam(team);
    setShowNumpad(true);
  };

  // --- FINE SALVATAGGIO ---

  // --- Funzioni punteggio ---


  // --- Fine incontro ---
  // Funzione per finire il match, eventualmente per tempo scaduto
  const finishMatch = (byTimeExpired = false) => {
    // Prima di navigare, assicuriamoci che lo stato di inversione sia stato correttamente persistito
    try {
      // Forzare un evento per notificare l'aggiornamento dell'inversione
      window.dispatchEvent(new CustomEvent('inversioneLatiChanged', {
        detail: { matchId: matchRealId, inverted: invertiLati }
      }));

      // Determina il vincitore basato sulle regole
      let winner;
      if (byTimeExpired) {
        // Se il tempo è scaduto, vince chi ha più punti (o chi ha la priorità in caso di parità)
        if (scoreA > scoreB) {
          winner = 1;
        } else if (scoreB > scoreA) {
          winner = 2;
        } else {
          // In caso di parità, controlla priorità
          if (priorityTeam === 'A') {
            winner = 1;
          } else if (priorityTeam === 'B') {
            winner = 2;
          } else {
            winner = null; // Parità senza priorità (caso raro)
          }
        }
      } else {
        // Altrimenti, regola normale (chi ha raggiunto il punteggio target)
        winner = scoreA > scoreB ? 1 : (scoreB > scoreA ? 2 : null);
      }

      console.log('Fine match - Dati:', {
        byTimeExpired,
        scoreA,
        scoreB,
        priorityTeam,
        winner
      });

      // MOSTRA RIEPILOGO FINALE SOLO PER SQUADRE/MISTE PRIMA DI NAVIGARE
      const competitionType = localStorage.getItem('competitionType') || 'individual';
      const isIndividual = competitionType === 'individual';

      const finalAssalti = [...assalti];
      // Assicurati che l'ultimo assalto abbia il punteggio finale
      if (finalAssalti[currentAssalto]) {
        finalAssalti[currentAssalto].scoreA = scoreA;
        finalAssalti[currentAssalto].scoreB = scoreB;
      }

      const currentFencer = finalAssalti[currentAssalto];
      const prevStart = currentAssalto > 0 ? finalAssalti[currentAssalto - 1] : { scoreA: 0, scoreB: 0 };

      // Mostra il popup solo se NON è individuale e se c'è stato almeno un punto
      const shouldShowSummary = !isIndividual && (scoreA > 0 || scoreB > 0);

      if (shouldShowSummary) {
        setBoutSummaryData({
          boutNumber: currentAssalto + 1,
          fencerA: typeof currentFencer?.fencerA === 'string' ? currentFencer.fencerA : currentFencer?.fencerA?.name || '',
          fencerB: typeof currentFencer?.fencerB === 'string' ? currentFencer.fencerB : currentFencer?.fencerB?.name || '',
          pointsA: scoreA - (prevStart?.scoreA || 0),
          pointsB: scoreB - (prevStart?.scoreB || 0),
          totalScoreA: scoreA,
          totalScoreB: scoreB,
          matchStats: calculateMatchStats(finalAssalti),
          isFinal: true
        });
        setShowBoutSummary(true);
      }

      // Se mostriamo il riepilogo finale, non navighiamo subito
      const showSummary = shouldShowSummary;

      // Un breve ritardo per consentire la propagazione dell'evento e eventuale visualizzazione popup
      setTimeout(() => {
        // Continua con il salvataggio
        if (bracketMode && typeof roundIdx === 'number' && typeof matchIdx === 'number') {
          const bracket = JSON.parse(localStorage.getItem('bracket') || '[]');
          if (Array.isArray(bracket) && bracket[roundIdx] && bracket[roundIdx][matchIdx]) {
            bracket[roundIdx][matchIdx] = {
              ...bracket[roundIdx][matchIdx],
              score1: scoreA,
              score2: scoreB,
              completed: true,
              byTimeExpired: byTimeExpired,
              winner: winner
            };
            saveBracket(bracket);
          }
          if (!showSummary) {
            if (location.state?.from === 'schedule') navigate('/schedule');
            else navigate('/bracket');
          }
          return;
        }
        // Modalità round robin
        if (typeof matchId === 'number') {
          const matches = loadMatches() || [];
          matches[matchId] = {
            ...matches[matchId],
            scoreA,
            scoreB,
            completed: true,
            byTimeExpired: byTimeExpired,
            winner: winner
          };
          saveMatches(matches);
          if (!showSummary) {
            if (location.state?.from === 'bracket') navigate('/bracket');
            else navigate('/schedule');
          }
          return;
        }
      }, 100);
    } catch (e) {
      console.error('Errore durante il salvataggio del match:', e);

      // Fallback in caso di errore
      if (bracketMode) {
        navigate('/bracket');
      } else {
        navigate('/schedule');
      }
    }
  };

  // Funzioni separate per gestire diversi tipi di fine match
  const confirmEndMatchByScore = () => { finishMatch(false); };
  const confirmEndMatchByTime = () => { finishMatch(true); };


  // Aggiungi questa funzione DOPO gli stati e PRIMA del render
  // const handleScoreChange = (fencer, delta) => {
  //   const opponent = fencer === 'A' ? 'B' : 'A';
  //   const newScore = fencer === 'A' ? scoreA + delta : scoreB + delta;
  //   const maxScore = getStoccateTotali();
  //   
  //   if (newScore <= maxScore) {
  //     if (fencer === 'A') {
  //       updateMatchScore(newScore, scoreB);
  //     } else {
  //       updateMatchScore(scoreA, newScore);
  //     }
  //   }
  // };

  // --- RENDER ---
  const theme = {
    primary: '#1976d2',
    secondary: '#388e3c',
    accent: '#ffe082',
    danger: '#d32f2f',
    bg: '#f7fafd',
    card: '#fff',
    border: '#e0e3ef',
    shadow: '0 2px 16px #0002',
  };

  const styles = StyleSheet.create({
    penaltyContainer: {
      flexDirection: 'row',
      justifyContent: 'center',
      marginBottom: 3, // Piccolo spazio tra cartellini e -1
    },
    penaltyCard: {
      width: 22,
      height: 22,
      marginHorizontal: 2,
      borderWidth: 1,
      borderColor: '#ccc',
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: 4,
    },
    yellowCard: {
      backgroundColor: '#ffeb3b', // Stesso colore del bordo
    },
    redCard: {
      backgroundColor: '#f44336', // Stesso colore del pulsante 'Torna ad assalti'
    },
    activeCard: {
      borderWidth: 2,
      borderColor: '#000',
    },
    minusButtonContainer: {
      width: 60, // Stessa larghezza del pulsante +1
      alignItems: 'center',
    },
    buttonRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%',
    },
    mobileOverlay: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center'
    },
    desktopOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 9999
    },
    closeXButton: {
      position: 'absolute',
      top: 40,
      right: 40,
      padding: 10,
    },
    closeXText: {
      fontSize: 40,
      color: '#000',
    },
    desktopCloseXButton: {
      position: 'absolute',
      top: '20px',
      right: '40px',
      backgroundColor: 'transparent',
      border: 'none',
      fontSize: '40px',
      cursor: 'pointer',
    },
    fullScreenTouchable: {
      flex: 1,
      width: '100%',
      height: '100%',
    },
    plusOneButtonRed: {
      borderRadius: 12,
      paddingVertical: 18, // Aumentato da 10 a 18
      paddingHorizontal: 18,
      marginHorizontal: 5,
      minWidth: 60,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#d32f2f' // Rosso
    },
    plusOneButtonGreen: {
      borderRadius: 12,
      paddingVertical: 18, // Aumentato da 10 a 18
      paddingHorizontal: 18,
      marginHorizontal: 5,
      minWidth: 60,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#388e3c' // Verde
    },
    minusOneButton: {
      borderRadius: 12,
      paddingVertical: 5,
      paddingHorizontal: 18,
      marginHorizontal: 5,
      minWidth: 60,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#e3e3e3'
    },
    scoreButtonText: {
      color: '#fff',
      fontSize: 22,
      fontWeight: 'bold'
    },
  });

  const PlatformOverlay = ({ color, onClose }) => {
    const isMobile = /Mobi|Android|iPhone/i.test(navigator.userAgent);

    const overlayStyle = {
      position: isMobile ? undefined : 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: color,
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 999999
    };

    if (isMobile) {
      return (
        <Modal visible transparent={true} animationType="fade" onRequestClose={onClose}>
          <TouchableOpacity
            style={{ flex: 1, backgroundColor: color }}
            activeOpacity={1}
            onPress={onClose}
          >
            <View style={overlayStyle} />
          </TouchableOpacity>
        </Modal>
      );
    } else {
      return (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: color,
            zIndex: 999999
          }}
          onClick={onClose}
        />
      );
    }
  };

  // Calcoli per il rendering
  const stoccateTotali = getStoccateTotali();
  const maxScore = Math.max(scoreA, scoreB);
  const nextThreshold = getNextThreshold();
  const isFineIncontro = isMatchFinished();

  // Ottieni il tipo di competizione per mostrare testi appropriati
  const competitionType = localStorage.getItem('competitionType') || 'individual';

  // Testi personalizzati in base al tipo di competizione
  const getCompetitionText = () => {
    if (competitionType === 'team') {
      return 'Stoccate al cambio';
    } else if (competitionType === 'individual' || competitionType === 'mixed') {
      return 'Stoccate rimanenti';
    } else {
      return 'Stoccate rimanenti';
    }
  };

  // DEBUG: logga gli assalti prima del rendering
  const normalizeAssalti = (assalti) => assalti.map(a => {
    const getName = (fencer, actual) => {
      if (actual) return typeof actual === 'string' ? actual : (actual.name || '?');
      if (fencer) return typeof fencer === 'string' ? fencer : (fencer.name || '?');
      return '?';
    };
    return {
      ...a,
      fencerA: getName(a.fencerA, a.actualFencerA),
      fencerB: getName(a.fencerB, a.actualFencerB)
    };
  });
  const assaltiNorm = normalizeAssalti(assalti);
  console.log('[DEBUG] Render ScoreKeeper - assalti:', assaltiNorm);

  return (
    <View style={{ flex: 1, backgroundColor: '#f5f5f5', alignItems: 'center', justifyContent: 'flex-start', minHeight: '100vh', width: '100%', maxWidth: '100%', paddingVertical: 24, paddingHorizontal: 8, overflow: 'hidden' }}>
      {/* Pulsante di inversione lati rimosso da qui e spostato sotto il VS */}

      {/* Titolo nello stesso stile di MatchSchedule */}
      <View style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', marginTop: 10 }}>
        <View style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
          {/* Icona SVG per il titolo - Personalizzata Scherma */}
          <FencingSwordsIcon style={{ fontSize: 44, color: theme.primary, marginRight: 12 }} />
          {/* Titolo */}
          <Text style={{ fontSize: 32, fontWeight: 'bold', color: theme.primary, margin: 0, textAlign: 'center', letterSpacing: 1, textShadow: '0px 2px 4px rgba(0,0,0,0.06)' }}>
            {isFineIncontro ? 'Incontro terminato' : 'Match in Corso'}
          </Text>
        </View>
        {/* Separatore blu */}
        <View style={{ width: 72, height: 4, backgroundColor: theme.primary, borderRadius: 2, marginTop: 2, marginBottom: 10, opacity: 0.2 }} />

        {/* Conteggio assalti (solo per gare a squadre) */}
        {competitionType === 'team' && assalti.length > 0 && (
          <Text style={{ fontSize: 18, color: theme.primary, fontWeight: '600', marginBottom: 10, opacity: 0.9 }}>
            Assalti: {Math.min(currentAssalto + 1, assalti.length)}/{assalti.length}
          </Text>
        )}

        {/* Pulsante Gestione Match (solo per gare a squadre) */}
        {competitionType === 'team' && assalti.length > 0 && (
          <TouchableOpacity
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              backgroundColor: '#fff',
              paddingVertical: 6,
              paddingHorizontal: 14,
              borderRadius: 20,
              borderWidth: 1,
              borderColor: '#1976d2',
              marginBottom: 8
            }}
            onPress={() => setShowMatchManager(true)}
          >
            <SettingsIcon style={{ fontSize: 18, color: '#1976d2', marginRight: 6 }} />
            <Text style={{ fontSize: 14, color: '#1976d2', fontWeight: '500' }}>Gestisci</Text>
          </TouchableOpacity>
        )}
      </View>


      {/* Squadre */}
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', marginBottom: 22, width: '100%' }}>
        {/* Utilizziamo getLato per invertire i team se necessario */}
        {(() => {
          const { visualTeamA, visualTeamB } = getLato(teamA, teamB);
          return (
            <>
              <View style={{
                backgroundColor: '#d32f2f',
                borderRadius: 16,
                paddingVertical: 10,
                paddingHorizontal: 12,
                marginRight: 10,
                width: 130,
                height: 70,
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Text style={{
                  color: '#fff',
                  fontSize: 18,
                  fontWeight: 'bold',
                  textAlign: 'center',
                  flexWrap: 'wrap',
                  width: '100%'
                }} numberOfLines={3} adjustsFontSizeToFit={true} minimumFontScale={0.6}>
                  {competitionType === 'individual'
                    ? ((visualTeamA?.name || '?').replace(/^\s*Squadra\s*/i, ''))
                    : `Squadra ${visualTeamA?.name || '?'}`}
                </Text>
              </View>

              {/* Pulsante inversione lati posizionato tra le card delle squadre */}
              <View style={{ marginHorizontal: 20, zIndex: 10 }}>
                <InvertiLatiButton
                  invertiLati={invertiLati}
                  toggleInvertiLati={toggleInvertiLati}
                  style={{
                    backgroundColor: 'rgba(25, 118, 210, 0.98)',
                    padding: 8,
                    borderRadius: 25,
                    width: 45,
                    height: 45,
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0px 2px 3px rgba(0, 0, 0, 0.25)'
                  }}
                />
              </View>

              <View style={{
                backgroundColor: '#388e3c',
                borderRadius: 16,
                paddingVertical: 10,
                paddingHorizontal: 12,
                marginLeft: 10,
                width: 130,
                height: 70,
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Text style={{
                  color: '#fff',
                  fontSize: 18,
                  fontWeight: 'bold',
                  textAlign: 'center',
                  flexWrap: 'wrap',
                  width: '100%'
                }} numberOfLines={3} adjustsFontSizeToFit={true} minimumFontScale={0.6}>
                  {competitionType === 'individual'
                    ? ((visualTeamB?.name || '?').replace(/^\s*Squadra\s*/i, ''))
                    : `Squadra ${visualTeamB?.name || '?'}`}
                </Text>
              </View>
            </>
          );
        })()}
      </View>


      {/* Tabella assalti */}
      <View style={{
        backgroundColor: '#fff',
        borderRadius: 12,
        boxShadow: '0px 2px 16px rgba(0, 0, 0, 0.1)',
        marginBottom: 30,
        padding: 10,
        width: '100%',
        maxWidth: 520,
        alignItems: 'center',
        alignSelf: 'center'
      }}>
        {/* Contenuto degli assalti */}
        {assalti.length === 0 ? (
          <Text style={{ color: '#888', fontSize: 18, textAlign: 'center' }}>Nessun assalto disponibile</Text>
        ) : assaltiNorm.map((assaltoOriginal, idx) => {
          // Applica l'inversione se necessario
          const assalto = getAssaltoInvertito(assaltoOriginal);
          const isCorrente = idx === currentAssalto;
          const nomeA = assalto.fencerA;
          const nomeB = assalto.fencerB;
          return (
            <View key={idx} style={{
              backgroundColor: isCorrente ? '#fffde7' : '#fff',
              borderRadius: isCorrente ? 10 : 7,
              marginVertical: isCorrente ? 10 : 2,
              alignItems: 'center',
              flexDirection: 'column',
              width: '100%',
              alignSelf: 'center',
              padding: isCorrente ? 14 : 10,
              justifyContent: 'space-between',
              borderWidth: isCorrente ? 2 : 1,
              borderColor: isCorrente ? '#ffd54f' : '#e0e3ef',
              boxShadow: isCorrente ? '0 2px 12px #ffd54f44' : undefined
            }}>
              {/* Header con nomi schermidori per assalto in corso */}
              {isCorrente && (
                <View style={{
                  flexDirection: 'row',
                  width: '100%',
                  justifyContent: 'space-between',
                  paddingHorizontal: 10,
                  backgroundColor: '#fafafa',
                  borderRadius: 8,
                  paddingVertical: 8,
                  marginBottom: 6
                }}>
                  <View style={{
                    width: '32%',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Text
                      style={{
                        fontWeight: 'bold',
                        color: '#d32f2f',
                        fontSize: nomeA.length > 10 ? Math.max(18 * (1 - (nomeA.length - 10) * 0.05), 12) : 18,
                        textAlign: 'center'
                      }}
                      numberOfLines={nomeA.includes(' ') ? 2 : 1}
                      ellipsizeMode="tail"
                    >
                      {nomeA}
                    </Text>
                  </View>

                  <View style={{
                    width: '36%',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {/* Punteggio invece di VS - usando la logica di inversione lati */}
                    <View style={{
                      flexDirection: 'row',
                      backgroundColor: '#fff',
                      paddingVertical: 8,
                      paddingHorizontal: 12,
                      borderRadius: 12,
                      justifyContent: 'center',
                      alignItems: 'center',
                      borderWidth: 2,
                      borderColor: '#e0e0e0',
                      boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.15)',
                    }}>
                      {(() => {
                        // Usa getLato per ottenere i punteggi visuali in base all'inversione
                        const { visualScoreA, visualScoreB } = getLato(teamA, teamB, scoreA, scoreB);

                        return (
                          <>
                            {/* Punteggio squadra A */}
                            <TouchableOpacity
                              onPress={() => openNumpad(invertiLati ? 'B' : 'A')}
                              style={{
                                padding: 4,
                                borderRadius: 8,
                                backgroundColor: ((invertiLati ? lastModifiedTeam === 'B' : lastModifiedTeam === 'A')) ?
                                  '#f0d0d0' : 'transparent',
                              }}
                            >
                              <Text style={{ fontWeight: 'bold', color: '#d32f2f', fontSize: 28, marginRight: 4 }}>{visualScoreA}</Text>
                            </TouchableOpacity>

                            <Text style={{ fontWeight: 'bold', color: '#555', fontSize: 24, marginHorizontal: 2 }}>-</Text>

                            {/* Punteggio squadra B */}
                            <TouchableOpacity
                              onPress={() => openNumpad(invertiLati ? 'A' : 'B')}
                              style={{
                                padding: 4,
                                borderRadius: 8,
                                backgroundColor: ((invertiLati ? lastModifiedTeam === 'A' : lastModifiedTeam === 'B')) ?
                                  '#d0f0d0' : 'transparent',
                              }}
                            >
                              <Text style={{ fontWeight: 'bold', color: '#388e3c', fontSize: 28, marginLeft: 4 }}>{visualScoreB}</Text>
                            </TouchableOpacity>
                          </>
                        );
                      })()}
                    </View>
                  </View>

                  <View style={{
                    width: '32%',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Text
                      style={{
                        fontWeight: 'bold',
                        color: '#388e3c',
                        fontSize: nomeB.length > 10 ? Math.max(18 * (1 - (nomeB.length - 10) * 0.05), 12) : 18,
                        textAlign: 'center'
                      }}
                      numberOfLines={nomeB.includes(' ') ? 2 : 1}
                      ellipsizeMode="tail"
                    >
                      {nomeB}
                    </Text>
                  </View>
                </View>
              )}

              {/* Nomi schermidori per assalti non correnti */}
              {!isCorrente && (
                <View style={{
                  flexDirection: 'row',
                  width: '100%',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#fafafa',
                  borderRadius: 8,
                  paddingVertical: 12, // Più spazio verticale
                  paddingHorizontal: 8,
                  marginBottom: 6
                }}>
                  <View style={{
                    flex: 1,
                    textAlign: 'center',
                    alignItems: 'center',
                    maxWidth: 120,
                    minWidth: 80
                  }}>
                    <Text style={{
                      fontWeight: 'bold',
                      color: '#d32f2f',
                      fontSize: 14,
                      textAlign: 'center',
                      flexWrap: 'wrap',
                      width: '100%'
                    }} numberOfLines={2} adjustsFontSizeToFit={true} minimumFontScale={0.7}>{nomeA}</Text>
                  </View>

                  <View style={{ marginHorizontal: 6 }}>
                    <Text style={{ fontSize: 20, color: '#555', fontWeight: 'bold' }}>-</Text>
                  </View>

                  <View style={{
                    flex: 1,
                    textAlign: 'center',
                    alignItems: 'center',
                    maxWidth: 120,
                    minWidth: 80
                  }}>
                    <Text style={{
                      fontWeight: 'bold',
                      color: '#388e3c',
                      fontSize: 14,
                      textAlign: 'center',
                      flexWrap: 'wrap',
                      width: '100%'
                    }} numberOfLines={2} adjustsFontSizeToFit={true} minimumFontScale={0.7}>{nomeB}</Text>
                  </View>
                </View>
              )}

              {/* Controlli punteggio solo per assalto corrente */}
              {isCorrente && (
                <View style={{
                  flexDirection: 'row',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flex: 2,
                  paddingHorizontal: 5,
                  width: '100%',
                }}>
                  {/* Team A (sinistra) */}
                  <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    {/* -1 e cartellini in colonna */}
                    <View style={styles.minusButtonContainer}>
                      {/* Cartellini sopra */}
                      <View style={styles.penaltyContainer}>
                        {/* Rosso prima, poi giallo */}
                        {(() => {
                          // Usa getLato per determinare quali cartellini visualizzare
                          const { visualPenaltiesA } = getLato(null, null, 0, 0, penalties.A, penalties.B);
                          const teamPenalties = visualPenaltiesA || penalties.A;

                          return (
                            <>
                              <View style={{ position: 'relative', marginBottom: 6 }}>
                                <TouchableOpacity
                                  style={[styles.penaltyCard, styles.redCard, teamPenalties.red > 0 && styles.activeCard]}
                                  onPress={() => handlePenalty('A', 'red')}
                                />
                                {teamPenalties.red > 0 && (
                                  <View style={{
                                    position: 'absolute', top: -8, right: -8, backgroundColor: '#d32f2f', borderRadius: 10, paddingHorizontal: 5, minWidth: 18, height: 18, justifyContent: 'center', alignItems: 'center', zIndex: 2
                                  }}>
                                    <Text style={{ color: '#fff', fontWeight: 'bold', fontSize: 12 }}>{teamPenalties.red}</Text>
                                  </View>
                                )}
                              </View>
                              <View style={{ position: 'relative' }}>
                                <TouchableOpacity
                                  style={[styles.penaltyCard, styles.yellowCard, teamPenalties.yellow > 0 && styles.activeCard]}
                                  onPress={() => handlePenalty('A', 'yellow')}
                                />
                                {teamPenalties.yellow > 0 && (
                                  <View style={{
                                    position: 'absolute', top: -8, right: -8, backgroundColor: '#ffd600', borderRadius: 10, paddingHorizontal: 5, minWidth: 18, height: 18, justifyContent: 'center', alignItems: 'center', zIndex: 2
                                  }}>
                                    <Text style={{ color: '#333', fontWeight: 'bold', fontSize: 12 }}>{teamPenalties.yellow}</Text>
                                  </View>
                                )}
                              </View>
                            </>
                          );
                        })()}
                      </View>

                      {/* -1 rosso sotto */}
                      <TouchableOpacity
                        style={styles.minusOneButton}
                        onPress={() => handleScoreChangeWithAssaltoCheck(invertiLati ? 'B' : 'A', -1)}
                      >
                        <Text style={{ color: '#d32f2f', fontSize: 22, fontWeight: 'bold' }}>-1</Text>
                      </TouchableOpacity>
                    </View>

                    {/* +1 rosso */}
                    <TouchableOpacity
                      style={styles.plusOneButtonRed}
                      onPress={() => handleScoreChangeWithAssaltoCheck(invertiLati ? 'B' : 'A', 1)}
                      disabled={(invertiLati ? scoreB : scoreA) >= stoccateTotali}
                    >
                      <Text style={styles.scoreButtonText}>+1</Text>
                    </TouchableOpacity>
                  </View>

                  {/* Spazio centrale - rimosso punteggio */}

                  {/* Team B (destra) */}
                  <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    {/* +1 verde */}
                    <TouchableOpacity
                      style={styles.plusOneButtonGreen}
                      onPress={() => handleScoreChangeWithAssaltoCheck(invertiLati ? 'A' : 'B', 1)}
                      disabled={(invertiLati ? scoreA : scoreB) >= stoccateTotali}
                    >
                      <Text style={styles.scoreButtonText}>+1</Text>
                    </TouchableOpacity>

                    {/* -1 e cartellini in colonna */}
                    <View style={styles.minusButtonContainer}>
                      {/* Cartellini sopra */}
                      <View style={styles.penaltyContainer}>
                        {(() => {
                          // Usa getLato per determinare quali cartellini visualizzare
                          const { visualPenaltiesB } = getLato(null, null, 0, 0, penalties.A, penalties.B);
                          const teamPenalties = visualPenaltiesB || penalties.B;

                          return (
                            <>
                              <View style={{ position: 'relative', marginBottom: 6 }}>
                                <TouchableOpacity
                                  style={[styles.penaltyCard, styles.yellowCard, teamPenalties.yellow > 0 && styles.activeCard]}
                                  onPress={() => handlePenalty('B', 'yellow')}
                                />
                                {teamPenalties.yellow > 0 && (
                                  <View style={{
                                    position: 'absolute', top: -8, right: -8, backgroundColor: '#ffd600', borderRadius: 10, paddingHorizontal: 5, minWidth: 18, height: 18, justifyContent: 'center', alignItems: 'center', zIndex: 2
                                  }}>
                                    <Text style={{ color: '#333', fontWeight: 'bold', fontSize: 12 }}>{teamPenalties.yellow}</Text>
                                  </View>
                                )}
                              </View>
                              <View style={{ position: 'relative' }}>
                                <TouchableOpacity
                                  style={[styles.penaltyCard, styles.redCard, teamPenalties.red > 0 && styles.activeCard]}
                                  onPress={() => handlePenalty('B', 'red')}
                                />
                                {teamPenalties.red > 0 && (
                                  <View style={{
                                    position: 'absolute', top: -8, right: -8, backgroundColor: '#d32f2f', borderRadius: 10, paddingHorizontal: 5, minWidth: 18, height: 18, justifyContent: 'center', alignItems: 'center', zIndex: 2
                                  }}>
                                    <Text style={{ color: '#fff', fontWeight: 'bold', fontSize: 12 }}>{teamPenalties.red}</Text>
                                  </View>
                                )}
                              </View>
                            </>
                          );
                        })()}
                      </View>

                      {/* -1 verde sotto */}
                      <TouchableOpacity
                        style={styles.minusOneButton}
                        onPress={() => handleScoreChangeWithAssaltoCheck(invertiLati ? 'A' : 'B', -1)}
                      >
                        <Text style={{ color: '#388e3c', fontSize: 22, fontWeight: 'bold' }}>-1</Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                </View>
              )}
            </View>
          );
        })}
      </View>
      {/* Stato assalto e punteggio */}
      <View style={{ padding: 16, backgroundColor: '#f5f5f5', borderRadius: 8, marginVertical: 18, alignItems: 'center', width: '100%', maxWidth: 520 }}>
        {isFineIncontro ? (
          <Text style={{ fontWeight: 'bold', color: '#388e3c', fontSize: 19, textAlign: 'center' }}>Fine incontro • Assalto {currentAssalto + 1} di {assalti.length}</Text>
        ) : (
          <Text style={{ fontWeight: 'bold', color: '#1976d2', fontSize: 19, textAlign: 'center' }}>{getCompetitionText()}: {nextThreshold - maxScore} • Assalto {currentAssalto + 1} di {assalti.length}</Text>
        )}
      </View>
      {/* Pulsanti fine o torna */}
      {/* Spacer per evitare che i pulsanti sticky coprano l'ultimo elemento */}
      <View style={{ height: 100 }} />

      {/* Pulsanti Sticky Bottom */}
      <View style={{
        position: 'fixed',
        bottom: 75, // Alzato per stare sopra la navbar (normalmente ~60px)
        left: 0,
        right: 0,
        maxWidth: 500,
        marginHorizontal: 'auto',
        paddingVertical: 10,
        paddingBottom: 0, // Padding bottom ridotto perché siamo già alti
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 40, // Spazio dai bordi laterali
        // gap rimosso perché usiamo space-between
        zIndex: 100,
        pointerEvents: 'box-none' // Permette di cliccare sotto se non si tocca i pulsanti (su web)
      }}>
        {/* Primo pulsante: Torna o Fine Incontro (Spunta) */}
        <TouchableOpacity
          style={{
            backgroundColor: isFineIncontro ? '#4caf50' : '#78909c',
            width: 60,
            height: 60,
            borderRadius: 30,
            alignItems: 'center',
            justifyContent: 'center',
            elevation: 8,
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.3,
            shadowRadius: 4,
          }}
          onPress={isFineIncontro ? confirmEndMatchByScore : () => {
            // Usa 'from' state per decidere dove tornare
            if (location.state?.from === 'bracket') {
              navigate('/bracket');
            } else if (location.state?.from === 'schedule') {
              navigate('/schedule');
            } else {
              // Fallback logica precedente
              bracketMode ? navigate('/bracket') : navigate('/schedule');
            }
          }}
        >
          {isFineIncontro ? (
            <CheckIcon style={{ color: '#fff', fontSize: 32 }} />
          ) : (
            <ArrowBackIcon style={{ color: '#fff', fontSize: 32 }} />
          )}
        </TouchableOpacity>

        {/* Secondo pulsante: Fine per tempo o Prossimo Assalto (Freccia Avanti) */}
        <TouchableOpacity
          style={{
            backgroundColor: '#ff9800',
            width: 60,
            height: 60,
            borderRadius: 30,
            alignItems: 'center',
            justifyContent: 'center',
            elevation: 8,
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.3,
            shadowRadius: 4,
          }}
          onPress={() => {
            // Nelle gare a squadre che non sono all'ultimo assalto, chiedi di passare al prossimo assalto
            if (competitionType === 'team' && currentAssalto < assalti.length - 1) {
              setShowNextAssaltoModal(true);
            } else {
              // Altrimenti (gara individuale o ultimo assalto a squadre)
              if (scoreA === scoreB) {
                setShowPriorityModal(true);
              } else {
                // Determina il vincitore attuale per il messaggio di conferma
                const cleanNameA = (teamA?.name || 'Squadra A').replace(/^\s*Squadra\s*/i, '');
                const cleanNameB = (teamB?.name || 'Squadra B').replace(/^\s*Squadra\s*/i, '');
                const winnerName = scoreA > scoreB ? cleanNameA : cleanNameB;

                // Popup di conferma vittoria
                Alert.alert(
                  "Conferma Vittoria",
                  `Stai per terminare l'incontro assegnando la vittoria a ${winnerName}. Confermi?`,
                  [
                    { text: "Annulla", style: "cancel" },
                    {
                      text: "Conferma",
                      onPress: () => confirmEndMatchByTime(),
                      style: "default"
                    }
                  ]
                );
              }
            }
          }}
        >
          <ArrowForwardIcon style={{ color: '#fff', fontSize: 32 }} />
        </TouchableOpacity>
      </View>

      {/* Numpad Modal - componente estratto */}
      <NumpadModal
        visible={showNumpad}
        onClose={() => {
          setShowNumpad(false);
          setActiveTeamInput(null);
          setTimeout(() => setLastModifiedTeam(null), 100);
        }}
        onConfirm={(value) => {
          const maxScore = getStoccateTotali();
          if (activeTeamInput === 'A') {
            if (value === maxScore && scoreB === maxScore) {
              updateMatchScore(value, maxScore - 1);
            } else {
              updateMatchScore(value, scoreB);
            }
            setLastModifiedTeam('A');
          } else if (activeTeamInput === 'B') {
            if (scoreA === maxScore && value === maxScore) {
              updateMatchScore(maxScore - 1, value);
            } else {
              updateMatchScore(scoreA, value);
            }
            setLastModifiedTeam('B');
          }
          setTimeout(() => setLastModifiedTeam(null), 2000);
        }}
        currentValue={activeTeamInput === 'A' ? scoreA : scoreB}
        maxValue={stoccateTotali}
        teamId={activeTeamInput}
        teamName={competitionType === 'team'
          ? `Squadra ${activeTeamInput}`
          : (activeTeamInput === 'A' ? teamA?.name : teamB?.name) || activeTeamInput}
        color={bracketMode ? '#1976d2' : (activeTeamInput === 'A' ? '#d32f2f' : '#388e3c')}
      />
      {activeCardColor && <PlatformOverlay
        color={activeCardColor}
        onClose={() => setActiveCardColor(null)}
      />}

      {/* Modal per conferma cambio assalto (solo per squadre) */}
      <NextAssaltoModal
        visible={showNextAssaltoModal}
        onClose={() => setShowNextAssaltoModal(false)}
        onConfirm={() => {
          setShowNextAssaltoModal(false);
          handleNextAssalto();
        }}
        currentAssalto={currentAssalto}
        scoreA={scoreA}
        scoreB={scoreB}
      />

      {/* Modal per priorità in caso di parità */}
      <PriorityModal
        visible={showPriorityModal}
        onClose={() => setShowPriorityModal(false)}
        onConfirm={() => {
          setShowPriorityModal(false);
          confirmEndMatchByTime();
        }}
        onSelectPriority={assignPriority}
        priorityTeam={priorityTeam}
        scoreA={scoreA}
        scoreB={scoreB}
        teamA={teamA}
        teamB={teamB}
        competitionType={competitionType}
      />

      {/* Modal per gestione match (ordine assalti, sostituzioni) */}
      <MatchManagerModal
        visible={showMatchManager}
        onClose={() => setShowMatchManager(false)}
        assalti={assalti}
        teamA={teamA}
        teamB={teamB}
        sostituzioni={sostituzioni}
        currentAssalto={currentAssalto}
        onUpdateAssalti={(newAssalti) => {
          setAssalti(newAssalti);
          // Salva anche nel match object
          const matches = loadMatches() || [];
          const matchIndex = matches.findIndex(m => m.id === matchRealId);
          if (matchIndex >= 0) {
            matches[matchIndex].assalti = newAssalti;
            matches[matchIndex].ordineModificato = true;
            saveMatches(matches);
          }
        }}
        onAddSostituzione={(sostituzione) => {
          const newSostituzioni = [...sostituzioni, sostituzione];
          setSostituzioni(newSostituzioni);
          // Salva anche nel match object
          const matches = loadMatches() || [];
          const matchIndex = matches.findIndex(m => m.id === matchRealId);
          if (matchIndex >= 0) {
            matches[matchIndex].sostituzioni = newSostituzioni;
            saveMatches(matches);
          }
        }}
      />

      {boutSummaryData && (
        <BoutSummaryModal
          visible={showBoutSummary}
          onClose={() => {
            setShowBoutSummary(false);
            // Se era il riepilogo finale, navighiamo ora
            if (boutSummaryData.isFinal) {
              const from = location.state?.from;
              if (from === 'bracket') {
                navigate('/bracket');
              } else if (from === 'schedule') {
                navigate('/schedule');
              } else if (bracketMode) {
                // Fallback se 'from' non è specificato ma sappiamo che è un tabellone
                navigate('/bracket');
              } else {
                navigate('/schedule');
              }
            }
          }}
          boutNumber={boutSummaryData.boutNumber}
          fencerA={boutSummaryData.fencerA}
          fencerB={boutSummaryData.fencerB}
          pointsA={boutSummaryData.pointsA}
          pointsB={boutSummaryData.pointsB}
          nextFencerA={boutSummaryData.nextFencerA}
          nextFencerB={boutSummaryData.nextFencerB}
          totalScoreA={boutSummaryData.totalScoreA}
          totalScoreB={boutSummaryData.totalScoreB}
          matchStats={boutSummaryData.matchStats}
          isFinal={boutSummaryData.isFinal}
        />
      )}
    </View>
  );
};

export default ScoreKeeper;